'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '../ui/button';
import { Menu, X, ChevronDown, Smartphone, Monitor, ArrowRight, BarChart3, Users, ReceiptText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { StoreDownloadLink } from '../analytics/StoreDownloadLink';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isFeaturesDropdownOpen, setIsFeaturesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const pathname = usePathname();
  const featuresDropdownRef = useRef<HTMLDivElement>(null);
  
  // Check if we're on the not-found page which needs special handling
  const isNotFoundPage = pathname === '/not-found';

  // Track scroll position
  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      setIsScrolled(latest > 50);
    });
    return () => unsubscribe();
  }, [scrollY]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (featuresDropdownRef.current && !featuresDropdownRef.current.contains(event.target as Node)) {
        setIsFeaturesDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Transform values for scroll-based animations
  const backgroundColor = useTransform(
    scrollY,
    [0, 100],
    isNotFoundPage
      ? ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0)']
      : ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0.98)']
  );
  
  const boxShadow = useTransform(
    scrollY,
    [0, 100],
    ['none', isNotFoundPage ? 'none' : '0 4px 16px rgba(0, 0, 0, 0.03)']
  );
  
  const translateY = useTransform(
    scrollY,
    [0, 100],
    ['0px', '16px']
  );

  const borderColor = useTransform(
    scrollY,
    [0, 100],
    isNotFoundPage
      ? ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0)']
      : ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 0.06)']
  );

  return (
    <div className="fixed top-0 left-0 right-0 w-full z-[100] px-md py-lg">
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={{
          backgroundColor,
          translateY,
          boxShadow,
          border: isNotFoundPage ? 'none' : '1px solid',
          borderColor,
        }}
        transition={{ duration: 0.6 }}
        className={cn(
          "relative w-full max-w-6xl mx-auto rounded-full px-lg sm:px-xl py-md border-border/50 transition-all duration-300",
          ""
        )}
      >
        {/* Skip Navigation Link */}
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-md focus:shadow-lg"
        >
          Skip to main content
        </a>
        <div className="flex items-center justify-between w-full">
          {/* Left side - Logo */}
          <Link href="/" className="flex items-center">
            <div className="flex items-center gap-3">
              <Image
                src="/logo/TipKeepr_app_icon.png"
                alt="TipKeepr Logo"
                width={56}
                height={56}
                className="rounded-lg"
              />
              <span className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                TipKeepr
              </span>
            </div>
          </Link>

          {/* Center - Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Features Dropdown */}
            <div className="relative" ref={featuresDropdownRef}>
              <button
                onClick={() => setIsFeaturesDropdownOpen(!isFeaturesDropdownOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsFeaturesDropdownOpen(!isFeaturesDropdownOpen);
                  }
                  if (e.key === 'Escape') {
                    setIsFeaturesDropdownOpen(false);
                  }
                }}
                aria-expanded={isFeaturesDropdownOpen}
                aria-haspopup="true"
                aria-controls="features-dropdown"
                className="flex items-center text-xl font-medium font-inter text-foreground"
              >
                Features
                <ChevronDown className={cn(
                  "ml-1 w-4 h-4 transition-transform duration-200",
                  isFeaturesDropdownOpen ? "rotate-180" : "rotate-0"
                )} />
              </button>
              
              <AnimatePresence>
                {isFeaturesDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    id="features-dropdown"
                    role="menu"
                    aria-labelledby="features-button"
                    className="absolute top-full left-0 mt-2 w-80 rounded-xl shadow-lg overflow-hidden bg-white border border-border/50"
                  >
                    <div className="py-1">
                      <Link href="/features" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <Smartphone className="w-4 h-4 mr-2" />
                          <span>All mobile features</span>
                        </div>
                      </Link>
                      <Link href="/features/tip-tracker-for-servers-bartenders" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <Smartphone className="w-4 h-4 mr-2" />
                          <span>Tip tracking</span>
                        </div>
                      </Link>
                      <Link href="/features/tip-income-analytics" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          <span>Income analytics</span>
                        </div>
                      </Link>
                      <Link href="/features/tip-pooling-tip-sheets" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <Users className="w-4 h-4 mr-2" />
                          <span>Team tip sheets</span>
                        </div>
                      </Link>
                      <Link href="/features/paycheck-wage-tax-tracking" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <ReceiptText className="w-4 h-4 mr-2" />
                          <span>Paychecks and tax summaries</span>
                        </div>
                      </Link>
                      <Link href="/features/serverlife-csv-import" onClick={() => setIsFeaturesDropdownOpen(false)}>
                        <div role="menuitem" className="flex items-center px-4 py-3 text-sm hover:bg-primary/5 transition-colors text-foreground">
                          <ArrowRight className="w-4 h-4 mr-2" />
                          <span>ServerLife CSV import</span>
                        </div>
                      </Link>
                      <Link href="/desktop-features" onClick={() => setIsFeaturesDropdownOpen(false)}>
                      <div className="flex items-center justify-between px-4 py-3 text-sm text-foreground/70 hover:bg-primary/5 transition-colors group">
                        <div className="flex items-center">
                          <Monitor className="w-4 h-4 mr-2 text-foreground/60" />
                          <span>Business Dashboard</span>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200 rounded-full shadow-sm">
                          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1.5 animate-pulse"></span>
                          Coming Soon
                        </span>
                      </div>
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Pricing Link */}
            <Link href="/pricing">
              <span className="text-xl font-medium font-inter text-foreground hover:text-primary transition-colors">
                Pricing
              </span>
            </Link>

            {/* Other Navigation Items */}
            {['About', 'Contact'].map((item) => (
              <Link key={item} href={`/${item.toLowerCase()}`}>
                <span 
                  className="text-xl font-medium font-inter text-foreground hover:text-foreground/70"
                >
                  {item}
                </span>
              </Link>
            ))}
          </div>

          {/* Right side - CTA Button */}
          <div className="hidden lg:block">
            <StoreDownloadLink placement="navbar_desktop">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-black text-white shadow-sm hover:shadow-md hover:bg-gray-800 transition-all duration-200">
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </StoreDownloadLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            className="lg:hidden p-xs rounded-full transition-colors text-foreground"
          >
            {isOpen ? (
              <X className="w-7 h-7" />
            ) : (
              <Menu className="w-7 h-7" />
            )}
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-[100]">
        {/* Backdrop */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-background/20"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Menu Panel */}
        <motion.div 
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          id="mobile-menu"
          className="absolute inset-y-0 right-0 w-full max-w-sm shadow-2xl px-lg py-xl bg-white"
        >
          <div className="flex items-center justify-end mb-xl">
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close navigation menu"
              className="p-xs rounded-full transition-colors hover:bg-muted/50 text-muted-foreground"
            >
              <X className="w-7 h-7" />
            </button>
          </div>

          <div className="flex flex-col gap-lg">
            {/* Features with sub-items */}
            <div className="space-y-md">
              <span className="block text-2xl font-medium text-muted-foreground">
                Features
              </span>
              <div className="pl-md space-y-sm">
                <Link href="/features" onClick={() => setIsOpen(false)}>
                  <div 
                    className="flex items-center text-xl text-muted-foreground/80"
                  >
                    <Smartphone className="w-5 h-5 mr-2" />
                    <span>All mobile features</span>
                  </div>
                </Link>
                <Link href="/features/tip-tracker-for-servers-bartenders" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center text-lg text-muted-foreground/80">
                    <Smartphone className="w-4 h-4 mr-2" />
                    <span>Tip tracking</span>
                  </div>
                </Link>
                <Link href="/features/tip-income-analytics" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center text-lg text-muted-foreground/80">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    <span>Income analytics</span>
                  </div>
                </Link>
                <Link href="/features/tip-pooling-tip-sheets" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center text-lg text-muted-foreground/80">
                    <Users className="w-4 h-4 mr-2" />
                    <span>Team tip sheets</span>
                  </div>
                </Link>
                <Link href="/features/paycheck-wage-tax-tracking" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center text-lg text-muted-foreground/80">
                    <ReceiptText className="w-4 h-4 mr-2" />
                    <span>Paychecks and tax summaries</span>
                  </div>
                </Link>
                <Link href="/features/serverlife-csv-import" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center text-lg text-muted-foreground/80">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    <span>ServerLife CSV import</span>
                  </div>
                </Link>
                <Link href="/desktop-features" onClick={() => setIsOpen(false)}>
                <div className="flex items-center justify-between text-xl text-muted-foreground/60">
                  <div className="flex items-center">
                    <Monitor className="w-5 h-5 mr-2 text-muted-foreground/50" />
                    <span>Business Dashboard</span>
                  </div>
                  <span className="inline-flex items-center px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border border-emerald-200 rounded-full shadow-sm">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                    Coming Soon
                  </span>
                </div>
                </Link>
              </div>
            </div>

            {/* Pricing */}
            <Link href="/pricing" onClick={() => setIsOpen(false)}>
              <span className="block text-2xl font-medium transition-colors text-surface-variant hover:text-surface-on">
                Pricing
              </span>
            </Link>

            {/* Other menu items */}
            {['About', 'Contact'].map((item) => (
              <Link key={item} href={`/${item.toLowerCase()}`} onClick={() => setIsOpen(false)}>
                <span 
                  className="block text-2xl font-medium transition-colors text-surface-variant hover:text-surface-on"
                >
                  {item}
                </span>
              </Link>
            ))}
            
            {/* Mobile Get Started Button */}
            <StoreDownloadLink placement="navbar_mobile" onClick={() => setIsOpen(false)}>
              <div className="flex items-center justify-center mt-4 px-6 py-3.5 rounded-full text-xl font-medium bg-black text-white shadow-sm hover:shadow-md hover:bg-gray-800 transition-all duration-200">
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </div>
            </StoreDownloadLink>
          </div>
        </motion.div>
      </div>
    )}
  </div>
  );
}
